import { useAtom, useAtomValue } from "jotai"
import { useTranslation } from "react-i18next"
import { Button } from "./ui/Button"
import { showSelectFolderDialog } from "@/store/project/project"
import {
	projectsAtom,
	projectsDisplayAtom,
	projectSearchInputAtom,
	projectsSortingAtom,
	sortedProjectsAtom,
} from "@/store/project/projects-state"
import { ProjectListItemView, ProjectThumbnail } from "@/components/ProjectThumbnail"
import {
	SampleProjectListItemView,
	SampleProjectThumbnail,
} from "@/components/SampleProjectThumbnail"
import React, { useEffect, useState } from "react"
import { showingSelectProjectDialogAtom } from "@/store/app-state"
import { ProjectGrouping } from "@/store/project/projects-service"
import clsx from "clsx"
import { Icon } from "./icons/Icon"
import { analytics } from "@/telemetry/amplitude"
import { useCreateNewProjectCardExperiment } from "./use-create-new-project-card-experiment"

const NoProjectsView: React.FC<{ showCreateButton: boolean }> = ({ showCreateButton }) => {
	const { t } = useTranslation("components")
	const projectSearchInput = useAtomValue(projectSearchInputAtom)
	const isSearching = projectSearchInput.length > 0
	return (
		<div className="flex size-full flex-col items-center justify-center space-y-6 text-center">
			<div className="text-md text-base-300">
				{isSearching ? t("projects.noProjectMatchesSearch") : t("projects.noProjects")}
			</div>
			{!isSearching && (
				<div className="w-1/2 text-sm text-base-400">
					{t("projects.projectsLinkedToFolders")}
				</div>
			)}
			{showCreateButton && (
				<Button
					trackingLabel="create_project_empty_state"
					aria-label={t("projects.ariaCreateNewProjectFromEmptyState")}
					variant="ghost"
					className="text-sm text-accent-600 hover:text-accent-500"
					onClick={() => showSelectFolderDialog()}
				>
					{t("projects.createNewProject")}
				</Button>
			)}
		</div>
	)
}

const AddProjectCard = () => {
	const { t } = useTranslation("components")
	return (
		<Button
			trackingLabel="create_project"
			trackingValue="create_project_card"
			variant="custom"
			size="custom"
			aria-label={t("projects.ariaCreateNewProjectFromProjectCard")}
			className="flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-base-700 hover:bg-base-600"
			onClick={() => showSelectFolderDialog()}
		>
			<Icon aria-hidden name="add" size={30} />
			<div aria-hidden className="text-sm font-semibold">
				{t("projects.createNewProject")}
			</div>
		</Button>
	)
}

const ProjectGroup: React.FC<{
	showCreateNewProjectCard: boolean
	projectGrouping: ProjectGrouping
}> = ({ showCreateNewProjectCard, projectGrouping: { data } }) => {
	const displayMode = useAtomValue(projectsDisplayAtom)

	return (
		<>
			{displayMode === "grid" && (
				<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xxl:grid-cols-6">
					{showCreateNewProjectCard && <AddProjectCard />}
					{data.map((project) =>
						project.isSampleProject ? (
							<SampleProjectThumbnail
								key={project.id}
								project={project}
								displayInfo
							/>
						) : (
							<ProjectThumbnail key={project.id} project={project} displayInfo />
						),
					)}
				</div>
			)}
			{displayMode === "list" && (
				<div className="flex flex-col space-y-2 py-2">
					{data.map((project) =>
						project.isSampleProject ? (
							<SampleProjectListItemView
								key={project.id}
								project={project}
								layout={projectListLayout}
							/>
						) : (
							<ProjectListItemView
								key={project.id}
								project={project}
								layout={projectListLayout}
							/>
						),
					)}
				</div>
			)}
		</>
	)
}

const ProjectListing: React.FC<{
	showCreateNewProjectCard: boolean
	showCreateButton: boolean
}> = ({ showCreateNewProjectCard, showCreateButton }) => {
	const { t } = useTranslation("components")
	const displayMode = useAtomValue(projectsDisplayAtom)
	const projectGroupings = useAtomValue(sortedProjectsAtom)
	if (projectGroupings.length === 0) {
		return <NoProjectsView showCreateButton={showCreateButton} />
	}

	// Join all project groupings into one
	const joinedGroup = projectGroupings.reduce<ProjectGrouping>(
		(prev, curr) => {
			prev.data.push(...curr.data)
			return prev
		},
		{ title: t("projects.allProjects"), data: [] },
	)

	return (
		<div className="flex w-full flex-col">
			{displayMode === "list" && <ProjectListViewHeader layout={projectListLayout} />}
			<ProjectGroup
				showCreateNewProjectCard={showCreateNewProjectCard}
				projectGrouping={joinedGroup}
				key={joinedGroup.title}
			/>
		</div>
	)
}

const ProjectListingHeader = (props: { disabled: boolean; showCreateButton: boolean }) => {
	const { t } = useTranslation("components")
	const [displayMode, setDisplayMode] = useAtom(projectsDisplayAtom)
	const [searchInput, setSearchInput] = useAtom(projectSearchInputAtom)
	const [isSearching, setIsSearching] = useState(false)

	const renderGridButton = () => {
		return (
			<Button
				trackingLabel="switch_to_grid_view"
				aria-label={t("projects.ariaSwitchProjectsToGridView")}
				variant="ghost"
				size="custom"
				className={clsx(
					displayMode === "grid"
						? "bg-base-700 text-base-100 hover:text-base-100 dark:bg-base-100 dark:text-base-800 dark:hover:text-base-800"
						: "text-base-100 hover:bg-base-800 hover:text-base-100",
					"size-8 hover:bg-opacity-100",
				)}
				onClick={() => setDisplayMode("grid")}
				disabled={props.disabled}
			>
				<Icon name="grid_view" size={16} />
			</Button>
		)
	}

	const renderListButton = () => {
		return (
			<Button
				trackingLabel="switch_to_list_view"
				aria-label={t("projects.ariaSwitchProjectsToListView")}
				variant="ghost"
				size="custom"
				className={clsx(
					displayMode === "list"
						? "bg-base-700 text-base-100 hover:text-base-100 dark:bg-base-100 dark:text-base-800 dark:hover:text-base-800"
						: "text-base-100 hover:bg-base-800 hover:text-base-100",
					"size-8 hover:bg-opacity-100",
				)}
				onClick={() => setDisplayMode("list")}
				disabled={props.disabled}
			>
				<Icon name="list" size={20} />
			</Button>
		)
	}

	const shouldShowSearchBar = searchInput.length > 0 || isSearching
	const renderSearch = () => {
		return (
			<div
				className={clsx(
					props.disabled && "pointer-events-none",
					"relative flex items-center",
				)}
				onMouseEnter={() => setIsSearching(true)}
				onMouseLeave={() => setIsSearching(false)}
			>
				<input
					className={clsx(
						!shouldShowSearchBar && "hidden",
						"absolute h-8 rounded-md bg-base-800 p-1.5 pl-6 text-sm",
					)}
					type="search"
					value={searchInput}
					onChange={(e) => {
						setSearchInput(e.target.value)
					}}
				/>
				<Icon
					role="button"
					aria-label={t("projects.ariaSearch")}
					className={clsx(
						shouldShowSearchBar && "pointer-events-none",
						props.disabled && "opacity-50",
						"absolute z-10 ml-1",
					)}
					name="search"
					placeholder={t("projects.placeholderSearchProjects")}
					size={16}
				/>
			</div>
		)
	}

	const renderCreateButton = () => {
		return (
			<Button
				trackingLabel="create_project"
				trackingValue="create_project_button"
				aria-label={t("projects.ariaCreateNewProject")}
				variant="primary"
				size="custom"
				className="px-4 py-2"
				onClick={() => showSelectFolderDialog()}
			>
				{t("projects.newProject")}
			</Button>
		)
	}

	return (
		<div className="-mt-1 mb-8 flex flex-row items-center justify-between">
			<div className="flex flex-row items-center space-x-1">
				<div
					className="mr-1 text-xl font-bold"
					aria-label={t("projects.ariaProjects")}
					aria-hidden
				>
					{t("projects.projects")}
				</div>

				{renderSearch()}
			</div>

			<div className="flex flex-row items-center space-x-2">
				{renderGridButton()}
				{renderListButton()}
				{props.showCreateButton && renderCreateButton()}
			</div>
		</div>
	)
}

const Projects: React.FC = () => {
	const { t } = useTranslation("components")
	const { data: projects } = useAtomValue(projectsAtom)
	const shouldShowSelectProjectDialog = useAtomValue(showingSelectProjectDialogAtom)
	const displayMode = useAtomValue(projectsDisplayAtom)
	const projectGroupings = useAtomValue(sortedProjectsAtom)
	const { exposure, isLoading, showCreateNewProjectCard, userId } =
		useCreateNewProjectCardExperiment()
	const hasVisibleProjects = projectGroupings.some((group) => group.data.length > 0)
	const { experimentKey, variant, assignmentSource } = exposure
	useEffect(() => {
		if (isLoading || displayMode !== "grid" || !hasVisibleProjects) return
		analytics.trackExperimentExposure({ experimentKey, variant, assignmentSource }, userId)
		if (assignmentSource === "backend" && variant !== "FALLBACK") {
			analytics.setUserProperties({ [experimentKey]: variant })
		}
	}, [
		isLoading,
		displayMode,
		hasVisibleProjects,
		experimentKey,
		variant,
		assignmentSource,
		userId,
	])
	useEffect(() => {
		if (shouldShowSelectProjectDialog) {
			showSelectFolderDialog()
		}
	}, [shouldShowSelectProjectDialog])

	return (
		<div
			aria-label={t("projects.ariaProjectsList")}
			className="flex w-full flex-col space-y-0 overflow-y-auto p-8"
		>
			<ProjectListingHeader disabled={projects.length === 0} showCreateButton={!isLoading} />
			<ProjectListing
				showCreateNewProjectCard={showCreateNewProjectCard}
				showCreateButton={!isLoading}
			/>
		</div>
	)
}

const projectListLayout = ["w-10", "flex-grow", "w-[12vw]", "w-[12vw]"]
const ProjectListViewHeader = (props: { layout: string[] }) => {
	const { t } = useTranslation("components")
	const [sortOption, setSortOption] = useAtom(projectsSortingAtom)

	return (
		<div className="relative flex flex-row items-center space-x-4 overflow-hidden rounded-md">
			<div
				className={clsx(props.layout[1], "overflow-hidden truncate text-sm font-semibold")}
			>
				{t("projects.name")}
			</div>

			<div className={clsx(props.layout[0])}></div>

			<div className={clsx(props.layout[2], "flex flex-row items-center space-x-1")}>
				<div className="text-xs text-base-400">{t("projects.date")}</div>
				{sortOption === "eventDateAscending" && (
					<Button
						trackingLabel="sort_projects_down"
						aria-label={t("projects.ariaSortProjectsOldestFirst")}
						className="p-1 hover:bg-base-700"
						variant="ghost"
						size="custom"
						onClick={() => setSortOption("eventDateDescending")}
					>
						<Icon name="arrow_up" size={14} />
					</Button>
				)}
				{sortOption === "eventDateDescending" && (
					<Button
						trackingLabel="sort_projects_up"
						aria-label={t("projects.ariaSortProjectsNewestFirst")}
						variant="ghost"
						className="p-1 hover:bg-base-700"
						size="custom"
						onClick={() => setSortOption("eventDateAscending")}
					>
						<Icon name="arrow_down" size={14} />
					</Button>
				)}
			</div>

			<div className={clsx(props.layout[3], "text-xs text-base-400")}>
				{t("projects.photoCount")}
			</div>
		</div>
	)
}
export default Projects
